/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import './styles.scss'
import LazyLoad from 'react-lazyload'
import csvjson from 'csvjson'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Waypoint } from 'react-waypoint'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { onlyUnique, getDataForDate } from '../_utils'
import { HeaderFromStandaloneRC } from 'components/HeaderFromStandalone'
import { useWindowSize } from './useSize'
import { ResidentChart, ResidentChartSmall } from 'components/ResidentChart'
import { ActivityGroup } from 'components/ActivityGroup'
import { Link } from '@ta-interaktiv/contentbricks'

// DE
// 2021/covid-ausbruch-in-altersheim-bellinzona
/**
const ARTICLE_ID = '808092737617'
const LANG = 'de'
import { DE as LOCALES } from '../_locales'
import { DE as IMAGES } from '../_images'
import { DE as TEXT } from '../_text'

const paragraphsAfterMap = 4
const paragraphsStartViz = 2
const paragraphsAfterViz = 4
const paragraphsUnusedAtTheEnd = 1
*/

// FR
// 2021/epidemie-covid-ems-bellinzone
const ARTICLE_ID = '588941050641'
const LANG = 'fr'
import { FR as LOCALES } from '../_locales'
import { FR as IMAGES } from '../_images'
import { FR as TEXT } from '../_text'

const paragraphsAfterMap = 2
const paragraphsStartViz = 4
const paragraphsAfterViz = 2
const paragraphsUnusedAtTheEnd = 0

//
const STARTDATE = '2020-03-05'

export function AppComponent() {
  const [drawdata, setdrawdata] = useState<drawDataType>()
  const [fullData, setFullData] = useState<rawDataType[]>()
  const [allDates, setAllDates] = useState<string[]>()
  const [activities, setActivities] = useState<rawActivityDataType>()
  const windowsize = useWindowSize()
  const isMobile = windowsize.isMobile
  useEffect(() => {
    const dir = 'https://ta-interaktiv.storage.googleapis.com/casa-anziani/'

    fetch(dir + 'altenheim_full.csv')
      .then(response => response.text())
      .then(csv => {
        const parsedCSV = csvjson.toObject(csv, {}).map((m: rawDataType) => ({
          date: m.date,
          death: +m.death,
          floor: +m.floor,
          healthy: +m.healthy,
          staff: +m.staff,
          infected: +m.infected
        }))
        const alldates = parsedCSV
          .map((m: rawDataType) => m.date)
          .filter(onlyUnique)

        setAllDates(alldates)
        setFullData(parsedCSV)
        setdrawdata(getDataForDate(parsedCSV, STARTDATE))
      })

    fetch(dir + 'activities.csv')
      .then(response => response.text())
      .then(csv => {
        const parsedCSV = csvjson
          .toObject(csv, {})
          .map((m: ActivityDataType) => ({
            amount: +m.amount,
            name: m.name,
            date: m.date
          }))
        const groupedByDay: rawActivityDataType = {}
        parsedCSV.forEach((e: ActivityDataType) => {
          if (!groupedByDay[e.date]) groupedByDay[e.date] = []
          groupedByDay[e.date].push({ c: +e.amount, n: e.name })
        })
        setActivities(groupedByDay)
      })
  }, [])
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <HeaderFromStandaloneRC updated={LOCALES.main.update} />

      <div className='ui vertical very fitted segment'>
        <div className='ui text container ta text'>
          <Paragraph paragraphs={TEXT[0][1]} />
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui  container'>
          <ImageComp
            file={IMAGES.list.intro.f}
            author={IMAGES.list.intro.a}
            subline={IMAGES.list.intro.s}
            alt={IMAGES.list.intro.alt}
          />
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container ta text'>
          <div className='text-before'>
            {TEXT[1][1].map((m, i) => {
              return (
                <React.Fragment key={i}>
                  <Paragraph paragraphs={[m]} />
                  {i === 0 && (
                    <img
                      src={require('images/map.svg')}
                      className='map'
                      width='60%'
                      alt='Map of switzerland. Ticino is marked.'
                    />
                  )}
                </React.Fragment>
              )
            })}
            {TEXT.slice(paragraphsAfterMap, paragraphsStartViz).map((m, i) => {
              return <Paragraph title={m[0].s} paragraphs={m[1]} key={i} />
            })}
          </div>
        </div>
      </div>
      <div className='ui vertical segment'>
        <div className='ui container'>
          <div className='ui text container key'>{LOCALES.main.disclaimer}</div>
          <div className='colcontainer'>
            <div className='graph-column'>
              <div className={`graph-container small`}>
                {drawdata && allDates && !isMobile && (
                  <ResidentChart
                    data={drawdata}
                    locales={LOCALES.localesMainChart}
                    lang={LANG}
                  />
                )}

                {drawdata && isMobile && allDates && (
                  <ResidentChartSmall
                    data={drawdata}
                    locales={LOCALES.localesMainChart}
                    lang={LANG}
                  />
                )}
              </div>
            </div>
            <div className='text-column ta text' id='textColumn'>
              {TEXT.slice(
                paragraphsStartViz,
                TEXT.length - paragraphsAfterViz
              ).map((m, i) => {
                return (
                  <Paragraph
                    key={i}
                    title={m[0].s}
                    paragraphs={m[1]}
                    date={m[0].d}
                    waypoint={
                      <Waypoint
                        topOffset={'88%'}
                        bottomOffset={'10%'}
                        onEnter={() =>
                          setdrawdata(getDataForDate(fullData, m[0].d))
                        }
                      />
                    }
                    activities={
                      activities &&
                      activities[m[0].d] && (
                        <ActivityGroup
                          data={activities[m[0].d]}
                          locales={LOCALES.localesActivities}
                          date={m[0].d}
                          isMobile={windowsize.isMobile}
                        />
                      )
                    }
                    img={
                      IMAGES.list[m[0].d] && (
                        <ImageComp
                          file={IMAGES.list[m[0].d].f}
                          author={IMAGES.list[m[0].d].a}
                          subline={IMAGES.list[m[0].d].s}
                          alt={IMAGES.list[m[0].d].alt || ''}
                        />
                      )
                    }
                  />
                )
              })}
            </div>
          </div>
        </div>
      </div>
      <div className='ui vertical very fitted segment'>
        <div className='ui text container ta text'>
          <div className='text-before'>
            {TEXT.slice(
              TEXT.length - paragraphsAfterViz,
              TEXT.length - paragraphsUnusedAtTheEnd
            ).map((m, i) => {
              if (IMAGES.list[m[0].d]) {
                return (
                  <React.Fragment key={i}>
                    <Paragraph
                      paragraphs={m[1].slice(0, m[1].length - 2)}
                      title={m[0].s}
                    />
                    {i === 0 && (
                      <ImageComp
                        file={IMAGES.list[m[0].d].f}
                        author={IMAGES.list[m[0].d].a}
                        subline={IMAGES.list[m[0].d].s}
                        alt={IMAGES.list[m[0].d].alt || ''}
                      />
                    )}
                    <Paragraph paragraphs={m[1].slice(m[1].length - 2)} />
                  </React.Fragment>
                )
              }
              return <Paragraph title={m[0].s} paragraphs={m[1]} />
            })}
          </div>
        </div>
      </div>

      <Footer
        locale={LANG}
        articleId={ARTICLE_ID}
        credits={LOCALES.feedback.credits}
        hidePortfolioTeaser
        questions={LOCALES.feedback.questions}
      />
    </div>
  )
}

const ImageComp = ({
  file,
  author,
  subline,
  classname,
  alt
}: {
  file: string
  author?: string
  subline?: string
  classname?: string
  alt?: string
}) => {
  return (
    <LazyLoad height={480} classNamePrefix={classname} once>
      <>
        <img src={require('images/' + file)} alt={alt} />
        {subline && <div className='imagesubline'>{subline}</div>}
        {author && <div className='imageauthor'>{author}</div>}
      </>
    </LazyLoad>
  )
}

const Paragraph = ({
  title,
  paragraphs,
  date,
  waypoint,
  activities,
  img
}: {
  title?: string
  paragraphs: string[]
  date?: string
  waypoint?: React.ReactChild
  activities?: React.ReactChild
  img?: React.ReactChild
}) => {
  return (
    <>
      {paragraphs.map((m, i) => {
        let text: string | JSX.Element | (string | JSX.Element)[] = m
        const regexMdLinks = /\[([^\[]+)\](\(.*\))/gm

        const matches = m.match(regexMdLinks)

        if (matches) {
          text = []
          let textparts = m
            .split('[')
            .join('###')
            .split(')')
            .join('###')
            .split('###')
          textparts.forEach(p => {
            if (p.includes('http')) {
              let link = p.split('](')
              text.push(
                <Link allowWordWrap href={link[1]} target={'_parent'}>
                  {link[0]}
                </Link>
              )
            } else {
              text.push(p)
            }
          })
        }

        return (
          <p>
            {i === 0 && title && <b>{title} &#183;</b>}

            {text}
            {date && waypoint && waypoint}
          </p>
        )
      })}
      {!img && activities && activities}
      {img && img}
    </>
  )
}
